import { ActionReducerMapBuilder, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { withLoadingReducer } from 'utils/reducerHandler'
import {
  PurchaseOrderCheckQuantityInitialStateInterface,
  PurchaseOrderInboundItemsQueryParamsInterface,
} from 'features/PurchaseOrder/@types/CheckQuantity'
import { getErrorMessage } from 'helpers/errorMsg'
import {
  deletePurchaseOrderInboundItem,
  fetchAccountInternalFeatureFlagByLocation,
  fetchFeatureFlagByLocation,
  fetchProductInventoryStatusList,
  fetchPurchaseOrderInboundDetails,
  fetchPurchaseOrderInboundItems,
  generatePurchaseOrderProductDetailToPrint,
  SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY,
  submitPurchaseOrderInboundAssetComplete,
  submitPurchaseOrderInboundItem,
  submitPurchaseOrderInboundNonAssetComplete,
  updateRackInboundItemDetail,
  verifyPurchaseOrderInboundItem,
  fetchProductInventoryStatusNotesList,
} from './thunk'

const initialState: PurchaseOrderCheckQuantityInitialStateInterface = {
  isLoading: false,
  featureFlagsLocation: {},
  purchaseOrderInboundDetails: null,
  purchaseOrderInboundItems: [],
  purchaseOrderInboundItemsQueryParams: {
    page_index: 1,
    page_size: 20,
  },
  itemValidationErrorMessage: '',
  purchaseOrderInboundItemVerificationResult: null,
  constants: {
    inventoryStatuses: [],
    inventoryStatusNotes: [],
  },
}

const slice = createSlice({
  name: SLICE_NAME_PURCHASE_ORDER_CHECK_QUANTITY,
  initialState,
  reducers: {
    resetPurchaseOrderCheckQuantityState: () => initialState,
    resetItemValidationErrorMessage: (state) => {
      state.itemValidationErrorMessage = ''
    },
    setPurchaseOrderInboundItemsQueryParams: (
      state,
      { payload }: PayloadAction<Partial<PurchaseOrderInboundItemsQueryParamsInterface>>,
    ) => {
      state.purchaseOrderInboundItemsQueryParams = {
        ...state.purchaseOrderInboundItemsQueryParams,
        ...payload,
      }
    },
    setGeneralIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
  },
  extraReducers: withLoadingReducer(
    (builder: ActionReducerMapBuilder<PurchaseOrderCheckQuantityInitialStateInterface>) => {
      builder.addCase(fetchFeatureFlagByLocation.fulfilled, (state, { payload, meta }) => {
        const { featureFlagName } = meta.arg
        state.featureFlagsLocation[featureFlagName] = payload.data.active
      })

      builder.addCase(
        fetchAccountInternalFeatureFlagByLocation.fulfilled,
        (state, { payload, meta }) => {
          const { featureFlagNames } = meta.arg
          const { hub_feature_flags = [] } = payload.data
          const featureFlags: PurchaseOrderCheckQuantityInitialStateInterface['featureFlagsLocation'] =
            {}

          featureFlagNames.forEach((name) => {
            const featureFlagExist = hub_feature_flags.find((flag) => flag.key === name)
            featureFlags[name] = featureFlagExist?.active ?? false
          })

          state.featureFlagsLocation = {
            ...state.featureFlagsLocation,
            ...featureFlags,
          }
        },
      )

      builder.addCase(fetchPurchaseOrderInboundDetails.fulfilled, (state, { payload }) => {
        state.purchaseOrderInboundDetails = payload.data
      })

      builder.addCase(fetchPurchaseOrderInboundItems.fulfilled, (state, { payload }) => {
        state.purchaseOrderInboundItems = payload.data
      })

      builder.addCase(verifyPurchaseOrderInboundItem.fulfilled, (state, { payload }) => {
        state.itemValidationErrorMessage = ''
        state.purchaseOrderInboundItemVerificationResult = payload.data
      })

      builder.addCase(verifyPurchaseOrderInboundItem.rejected, (state, { payload }) => {
        const errorMessage = getErrorMessage(payload)
        state.itemValidationErrorMessage = errorMessage
        state.purchaseOrderInboundItemVerificationResult = null
      })

      builder.addCase(fetchProductInventoryStatusList.fulfilled, (state, { payload }) => {
        state.constants.inventoryStatuses = payload.data.status
      })

      builder.addCase(fetchProductInventoryStatusNotesList.fulfilled, (state, { payload }) => {
        state.constants.inventoryStatusNotes = payload.data
      })

      builder.addCase(submitPurchaseOrderInboundItem.fulfilled, (state) => {
        state.itemValidationErrorMessage = ''
        state.purchaseOrderInboundItemVerificationResult = null
      })
    },
    [
      deletePurchaseOrderInboundItem,
      fetchPurchaseOrderInboundDetails,
      fetchPurchaseOrderInboundItems,
      submitPurchaseOrderInboundItem,
      updateRackInboundItemDetail,
      verifyPurchaseOrderInboundItem,
      generatePurchaseOrderProductDetailToPrint,
      submitPurchaseOrderInboundAssetComplete,
      submitPurchaseOrderInboundNonAssetComplete,
    ],
  ),
})

export const {
  resetPurchaseOrderCheckQuantityState,
  resetItemValidationErrorMessage,
  setPurchaseOrderInboundItemsQueryParams,
  setGeneralIsLoading,
} = slice.actions

export default slice.reducer
